import { cn } from "@newxsystems/shared";
import { resolveKaajPrimitive } from "../../../../client";
import type { FooterBlockContact, FooterBlockLink, IFooterBlockProps } from "./FooterBlock.props";

const Image = resolveKaajPrimitive("Image");
const Link = resolveKaajPrimitive("Link");

const safeRel = (target?: string) => (target === "_blank" ? "noopener noreferrer" : undefined);

const FooterLink = ({ link, className }: { link: FooterBlockLink; className?: string }) => {
  if (!link.href || !link.text) {
    return null;
  }

  return (
    <Link
      href={link.href}
      target={link.target}
      rel={safeRel(link.target)}
      aria-label={link.ariaLabel}
      className={cn(
        "inline-flex min-h-9 items-center rounded-sm py-1 text-sm font-semibold leading-5 text-[#DCE6FF] underline decoration-transparent underline-offset-[0.34em] transition-[color,text-decoration-color,background-color,border-color,box-shadow] duration-200 hover:text-white hover:decoration-current focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#8B9CFF] motion-reduce:transition-none max-sm:min-h-11",
        className,
      )}
    >
      {link.text}
    </Link>
  );
};

export const FooterBlock = ({
  parentClass,
  logo,
  description,
  groups,
  socialLinks,
  contact,
  legalLinks,
  copyright,
}: IFooterBlockProps) => {
  const logoSrc = logo?.defaultSrc;
  const logoAlt = logo?.alternateText;
  const footerGroups = groups ?? [];
  const footerSocialLinks = socialLinks ?? [];
  const footerContact: FooterBlockContact | null | undefined = contact;
  const footerLegalLinks = legalLinks ?? [];
  const contactAddress = footerContact?.address;
  const visibleGroups = footerGroups;
  const hasContact = Boolean(
    footerContact && (contactAddress || footerContact.email || footerContact.phone),
  );
  const hasBrand = Boolean(logoSrc || description || footerSocialLinks.length > 0);

  if (
    !hasBrand &&
    visibleGroups.length === 0 &&
    !hasContact &&
    footerLegalLinks.length === 0 &&
    !copyright
  ) {
    return null;
  }

  return (
    <footer
      className={cn(
        "relative isolate overflow-hidden border-t border-[#1C2546] bg-[#080B1A] text-white",
        parentClass,
      )}
    >
      <div className="kaaj-container relative py-12 lg:py-16">
        <div
          className={cn(
            "grid gap-10 lg:gap-14",
            hasBrand ? "lg:grid-cols-[minmax(16rem,0.86fr)_minmax(0,1.64fr)]" : "lg:grid-cols-1",
          )}
        >
          {hasBrand ? (
            <section aria-label="Company" className="min-w-0">
              <div className="flex max-w-[34rem] flex-col items-start">
                {logoSrc ? (
                  <Link
                    aria-label={logoAlt || "Go to home page"}
                    className="group/logo inline-flex rounded-[1.35rem] p-1 outline-none focus-visible:ring-2 focus-visible:ring-[#8B9CFF] focus-visible:ring-offset-4 focus-visible:ring-offset-[#080B1A]"
                    href="/"
                  >
                    <span className="flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-[1.05rem] border border-white/18 bg-white p-2 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.55)] transition-colors duration-200 group-hover/logo:border-[#8B9CFF] motion-reduce:transition-none">
                      <Image
                        src={logoSrc}
                        alt={logoAlt ?? ""}
                        width={logo?.width ?? undefined}
                        height={logo?.height ?? undefined}
                        className="h-full w-full"
                        imageClassName="h-full w-full object-contain"
                        loading="lazy"
                        responsiveProfile="compact"
                        sizes="4rem"
                        srcSet={logo?.srcSet ?? undefined}
                      />
                    </span>
                  </Link>
                ) : null}

                {description ? (
                  <p className="mt-6 max-w-md text-pretty text-[0.96rem] leading-7 text-[#DCE6FF] [overflow-wrap:anywhere]">
                    {description}
                  </p>
                ) : null}

                {footerSocialLinks.length > 0 ? (
                  <nav aria-label="Social links" className="mt-6">
                    <ul className="flex flex-wrap gap-2.5">
                      {footerSocialLinks.map((link, index) => (
                        <li key={`${link.platform}-${link.href}-${index}`}>
                          <FooterLink
                            link={link}
                            className="rounded-full border border-white/12 bg-white/[0.04] px-3.5 text-[0.72rem] font-extrabold uppercase tracking-[0.14em] text-[#C8D2FF] no-underline shadow-[inset_0_1px_0_rgba(255,255,255,0.07)] hover:border-[#8B9CFF]/70 hover:bg-white/[0.08] hover:decoration-transparent"
                          />
                        </li>
                      ))}
                    </ul>
                  </nav>
                ) : null}
              </div>
            </section>
          ) : null}

          <div className="grid min-w-0 gap-x-8 gap-y-9 sm:grid-cols-2 lg:grid-cols-3">
            {visibleGroups.map((group, index) => (
              <nav aria-label={group.header} className="min-w-0" key={`${group.header}-${index}`}>
                {group.header ? (
                  <h2 className="text-xs font-extrabold uppercase tracking-[0.18em] text-[#AEBBFF] [overflow-wrap:anywhere]">
                    {group.header}
                  </h2>
                ) : null}
                {group.links.length > 0 ? (
                  <ul className="mt-3.5 grid gap-1">
                    {group.links.map((link, linkIndex) => (
                      <li key={`${link.text}-${link.href}-${linkIndex}`}>
                        <FooterLink link={link} className="[overflow-wrap:anywhere]" />
                      </li>
                    ))}
                  </ul>
                ) : null}
              </nav>
            ))}
          </div>
        </div>

        {hasContact ? (
          <address className="mt-9 flex max-w-4xl flex-wrap gap-x-6 gap-y-2 text-pretty text-sm leading-6 text-[#B9C6EA] not-italic md:mt-10">
            {contactAddress ? (
              <span className="[overflow-wrap:anywhere]">{contactAddress}</span>
            ) : null}
            {footerContact?.email ? (
              <a
                className="inline-flex min-h-11 items-center text-[#DCE6FF] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#8B9CFF]"
                href={`mailto:${footerContact.email}`}
              >
                {footerContact.email}
              </a>
            ) : null}
            {footerContact?.phone ? (
              <a
                className="inline-flex min-h-11 items-center text-[#DCE6FF] underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-[#8B9CFF]"
                href={`tel:${footerContact.phone}`}
              >
                {footerContact.phone}
              </a>
            ) : null}
          </address>
        ) : null}

        {(footerLegalLinks.length > 0 || copyright) && (
          <div className="mt-6 border-t border-white/10 pt-6 text-sm text-[#A7B4D4] md:mt-8">
            <div className="flex flex-col gap-5 md:flex-row md:items-start md:justify-between">
              {copyright ? (
                <p className="max-w-md leading-6 [overflow-wrap:anywhere]">{copyright}</p>
              ) : null}
              {footerLegalLinks.length > 0 ? (
                <nav aria-label="Legal links">
                  <ul className="flex flex-wrap gap-x-5 gap-y-1.5 md:justify-end">
                    {footerLegalLinks.map((link, index) => (
                      <li key={`${link.text}-${link.href}-${index}`}>
                        <FooterLink
                          link={link}
                          className="min-h-8 text-[#A7B4D4] max-sm:min-h-10"
                        />
                      </li>
                    ))}
                  </ul>
                </nav>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};

FooterBlock.displayName = "Block::FooterBlock";
